import { useState, useEffect, useRef, useCallback } from "react";

export function useGlobalDragDrop({
  onFilesDrop = () => {},
  accept = [],
  multiple = true,
} = {}) {
  const [isDragging, setIsDragging] = useState(false);

  const onFilesDropRef = useRef(onFilesDrop);
  const acceptRef = useRef(accept);
  const multipleRef = useRef(multiple);

  useEffect(() => {
    onFilesDropRef.current = onFilesDrop;
  }, [onFilesDrop]);

  useEffect(() => {
    acceptRef.current = accept;
  }, [accept]);

  useEffect(() => {
    multipleRef.current = multiple;
  }, [multiple]);

  const dragCounter = useRef(0);

  const hasFiles = (dataTransfer) =>
    dataTransfer?.types?.includes("Files") ?? false;

  const isAccepted = useCallback((files) => {
    const patterns = acceptRef.current;
    if (!patterns.length) return true;
    return Array.from(files).every((file) =>
      patterns.some((pat) =>
        pat.endsWith("/*")
          ? file.type.startsWith(pat.slice(0, -1))
          : file.type === pat,
      ),
    );
  }, []); // acceptRef never changes identity → stable

  const resetDrag = useCallback(() => {
    dragCounter.current = 0;
    setIsDragging(false);
  }, []);

  const handleDragEnter = useRef((e) => {
    if (!hasFiles(e.dataTransfer)) return;
    e.preventDefault();
    // Gate overlay on accepted types as early as possible
    if (!isAccepted(e.dataTransfer.items ?? e.dataTransfer.files)) return;
    dragCounter.current += 1;
    if (dragCounter.current === 1) setIsDragging(true);
  });

  const handleDragOver = useRef((e) => {
    if (!hasFiles(e.dataTransfer)) return;
    e.preventDefault(); // required to allow drop
    e.dataTransfer.dropEffect = "copy";
  });

  const handleDragLeave = useRef((e) => {
    if (!hasFiles(e.dataTransfer)) return;
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0; // guard against going negative
      setIsDragging(false);
    }
  });

  const handleDrop = useRef((e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);

    const { files } = e.dataTransfer;
    if (!files?.length) return;
    if (!isAccepted(files)) return;

    const fileList = multipleRef.current ? Array.from(files) : [files[0]];
    //@ts-ignore
    onFilesDropRef.current?.(fileList);
  });

  // ─── single effect — registers once, cleans up on unmount ─────────────────
  useEffect(() => {
    const enter = handleDragEnter.current;
    const over = handleDragOver.current;
    const leave = handleDragLeave.current;
    const drop = handleDrop.current;

    // dragover must be non-passive because we call preventDefault()
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragover", over, { passive: false });
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", drop);

    // Reset if the user switches tabs mid-drag (browser fires no dragleave).
    // "hidden" fires before the tab visually disappears — reliable cross-browser.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") resetDrag();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup — runs only on unmount
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragover", over);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", drop);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []); // empty deps — handlers read from stable refs, never re-register

  return { isDragging };
}
